import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Star as StarIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import intermittentFastingBackground from '../../images/intermittent-fasting.png';
import intermittentFastingPlanImage from '../../images/intermittent-fasting-plan.webp';
import infographicDietsImage from '../../images/infographic-diets.avif';
import intermittentFastingHowToImage from '../../images/intermittent-fasting-how-to-do.jpg';

const PAGE_URL = 'https://createadish.com/dietary-guide/intermittent-fasting';
const PAGE_TITLE = 'Intermittent Fasting Guide: When You Eat Matters - Create A Dish';
const PAGE_DESCRIPTION = 'An eating pattern that cycles between periods of fasting and eating, focusing more on when you eat rather than what you eat.';
const PAGE_IMAGE = 'https://createadish.com/images/intermittent-fasting-og.png';

const IntermittentFasting: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="intermittent fasting, IF, fasting, eating pattern, weight loss, lifestyle diet, time-restricted eating" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Intermittent Fasting Guide: When You Eat Matters",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": { "@type": "Organization", "name": "Create A Dish" },
            "publisher": { "@type": "Organization", "name": "Create A Dish", "logo": { "@type": "ImageObject", "url": "https://createadish.com/logo.png" } },
            "datePublished": "2025-12-24",
            "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL },
            "keywords": "Lifestyle, WeightLoss, Wellness"
          })}
        </script>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ height: { xs: '40vh', md: '50vh' }, backgroundImage: `url(${intermittentFastingBackground})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(1px)', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)', px: 2, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', mb: 2 }}>Intermittent Fasting</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>When You Eat Matters</Typography>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              onClick={() => navigate('/dietary-guide')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Dietary Guide
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Intermittent Fasting
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', color: 'primary.main' }}>Intermittent Fasting (IF)</Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.1rem' } }}>{PAGE_DESCRIPTION}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            {['#Lifestyle', '#WeightLoss', '#Wellness'].map(tag => <Typography key={tag} variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{tag}</Typography>)}
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to Intermittent Fasting (IF), an eating pattern that cycles between periods of fasting and eating, focusing more on when you eat rather than what you eat. This approach can support weight management, metabolic health, and overall wellness without strict food restrictions.
          </Typography>
          <Box component="img" src={intermittentFastingPlanImage} alt="Intermittent fasting plan" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Intermittent fasting has ancient roots, practiced for centuries in various cultures and religions for spiritual, health, and survival reasons. As early as the fifth century BCE, Hippocrates recommended fasting for healing. In modern times, it gained scientific attention in the early 20th century for metabolic health.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Methods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Intermittent fasting isn't about specific foods but timing your meals. Common methods include:
            </Typography>
            
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>16/8 Method</strong>: Fast for 16 hours, eat within an 8-hour window (e.g., noon to 8 p.m.).</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>5:2 Diet</strong>: Eat normally 5 days, restrict to 500-600 calories on 2 non-consecutive days.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Eat Stop Eat</strong>: Fast for 24 hours, 1-2 days a week.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Alternate-Day Fasting</strong>: Eat every other day or restrict calories on fasting days.</Typography>
            </Box>
            <Box component="img" src={infographicDietsImage} alt="Infographic diets" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Scientific evidence suggests intermittent fasting offers several health advantages, primarily through weight loss and metabolic improvements. It can reduce body weight, improve insulin resistance, lower cardiovascular risks, and decrease inflammation.
            </Typography>
            <Box component="img" src={intermittentFastingHowToImage} alt="How to do intermittent fasting" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting Intermittent Fasting
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Consult a Doctor</strong>: Check with a healthcare professional before starting.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Start Small</strong>: Begin with 12-hour fasts and gradually increase.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Stay Hydrated</strong>: Drink water, tea, or black coffee during fasts.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Choose Nutrient-Dense Foods</strong>: Focus on filling, balanced meals in your eating window.</Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>Ready to Create Your Perfect Dish?</Typography>
            <Button variant="contained" size="large" startIcon={<StarIcon />} onClick={() => { setMode('generate'); navigate('/composer'); }} sx={{ minWidth: 200, fontSize: '1.1rem', py: 1.5, px: 4 }}>Get Started</Button>
          </Box>
          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">© 2025 Create A Dish. Made with ❤️ for food lovers everywhere.</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default IntermittentFasting;
